import { useEffect, useMemo } from 'react';
import { atom, useRecoilState } from 'recoil';
import { getDeliveryConfig } from '../../api/deliveryConfigService';

const weekdays = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
const hebrewMonths = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const DEFAULT_MIN_BUSINESS_DAYS = 8;
const DEFAULT_MAX_BUSINESS_DAYS = 9;
const DEFAULT_WEEKEND = new Set([5, 6]);

export const deliveryRangeState = atom({
  key: 'deliveryRange',
  default: { computed: null, byFrame: null, status: 'idle' }
});

const buildDateInfo = (d) => ({
  weekday: d.getDay(),
  day: d.getDate(),
  month: d.getMonth(),
  year: d.getFullYear()
});

function computeLocalFallback() {
  const cursor = new Date();
  cursor.setHours(0, 0, 0, 0);
  let counted = 0;
  let start = null;
  let end = null;
  for (let i = 0; i < 365; i += 1) {
    cursor.setDate(cursor.getDate() + 1);
    if (DEFAULT_WEEKEND.has(cursor.getDay())) continue;
    counted += 1;
    if (counted === DEFAULT_MIN_BUSINESS_DAYS) start = new Date(cursor);
    if (counted === DEFAULT_MAX_BUSINESS_DAYS) {
      end = new Date(cursor);
      break;
    }
  }
  if (!start) start = end ? new Date(end) : new Date();
  if (!end) end = new Date(start);
  return {
    start: buildDateInfo(start),
    end: buildDateInfo(end),
    isOverride: false
  };
}

const formatRange = (computed) => {
  if (!computed) return null;
  const { start, end } = computed;
  const startDayName = weekdays[start.weekday];
  const endDayName = weekdays[end.weekday];
  const startMonth = hebrewMonths[start.month];
  const endMonth = hebrewMonths[end.month];

  const mobileRange =
    start.month === end.month
      ? `${start.day} - ${end.day} ב${endMonth}`
      : `${start.day} ב${startMonth} - ${end.day} ב${endMonth}`;

  const desktopRange = `יום ${startDayName}, ${start.day} ב${startMonth} - יום ${endDayName}, ${end.day} ב${endMonth}`;

  const cartDate = ` ${endDayName} ה${end.day} ב${endMonth} `;

  const pad2 = (n) => String(n).padStart(2, '0');
  const paymentSuccessDate = `${pad2(end.day)}.${pad2(end.month + 1)}.${end.year}`;

  return { mobileRange, desktopRange, cartDate, paymentSuccessDate };
};

export default function useDeliveryRange(frameType) {
  const [state, setState] = useRecoilState(deliveryRangeState);

  useEffect(() => {
    if (state.status !== 'idle') return;
    // No cancellation flag: deliveryRangeState is a global Recoil atom, so if
    // the component unmounts mid-fetch we still want the resolved value written
    // — otherwise the atom stays at 'loading' forever and the next mount sees
    // it as non-idle, skips the effect, and we're stuck on the local fallback.
    setState((prev) => ({ ...prev, status: 'loading' }));
    getDeliveryConfig()
      .then((data) => {
        if (data && data.computed) {
          setState({
            computed: data.computed,
            byFrame: data.computedByFrame || null,
            status: 'ready'
          });
        } else {
          setState({ computed: null, byFrame: null, status: 'error' });
        }
      })
      .catch(() => {
        setState({ computed: null, byFrame: null, status: 'error' });
      });
  }, [state.status, setState]);

  const fallback = useMemo(() => formatRange(computeLocalFallback()), []);
  const frameSpecific = frameType && state.byFrame ? state.byFrame[frameType] : null;
  const formatted = formatRange(frameSpecific || state.computed);
  return formatted || fallback;
}
